import React, { Component, ErrorInfo, ReactNode } from 'react';
import Button from '../../UI/Button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary component for catching and handling React component errors
 * Provides fallback UI and error recovery mechanisms
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Update state with error info
    this.setState({
      error,
      errorInfo
    });

    // Call onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    // Reset error state to retry rendering
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleReload = () => {
    // Reload the page as a last resort
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="error-boundary">
          <div className="container mx-auto">
            <div className="flex flex-wrap justify-center">
              <div className="md:w-8/12 lg:w-6/12">
                <div className="bg-white rounded-lg shadow border border-red-500">
                  <div className="px-4 py-3 border-b bg-gray-50 rounded-t-lg bg-red-500 text-white">
                    <h4 className="mb-0">
                      <i className="fas fa-exclamation-triangle mr-2"></i>
                      Something went wrong
                    </h4>
                  </div>
                  <div className="p-4">
                    <p className="mb-3">
                      An unexpected error occurred while rendering this component. 
                      This might be a temporary issue.
                    </p>
                    
                    {process.env.NODE_ENV === 'development' && this.state.error && (
                      <div className="mb-3">
                        <details className="mb-2">
                          <summary className="btn btn-outline-secondary btn-sm">
                            Show Error Details
                          </summary>
                          <div className="mt-2 p-2 bg-gray-100 border rounded">
                            <strong>Error:</strong> {this.state.error.message}
                            <br />
                            <strong>Stack:</strong>
                            <pre className="mt-1 small text-gray-500">
                              {this.state.error.stack}
                            </pre>
                            {this.state.errorInfo && (
                              <>
                                <strong>Component Stack:</strong>
                                <pre className="mt-1 small text-gray-500">
                                  {this.state.errorInfo.componentStack}
                                </pre>
                              </>
                            )}
                          </div>
                        </details>
                      </div>
                    )}

                    <div className="flex gap-2 flex-wrap">
                      <Button
                        primary
                        onClick={this.handleRetry}
                      >
                        <i className="fas fa-redo mr-1"></i>
                        Try Again
                      </Button>
                      <Button
                        secondary
                        onClick={this.handleReload}
                      >
                        <i className="fas fa-refresh mr-1"></i>
                        Reload Page
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;